// Split from djValueAdd.ts (I2). Shared local runtime helpers kept per-file for isolation.
import { SeqCounter } from "@djsem/shared";

const AT = "1970-01-01T00:00:00.000Z";
const _seq = new SeqCounter();

// ============================================================================
// PROMPT 48 — Gig Pack Generator
// ============================================================================

/** The kind of gig a gig pack is built for; selects which checklist template is generated. */
export type DJSEMGigPackType =
  | "wedding" | "bar" | "small_club" | "festival" | "corporate"
  | "private_party" | "release_show" | "streaming_set" | "venue_residency" | "custom";

/** The functional area a gig pack checklist item belongs to. */
export type DJSEMGigPackChecklistCategory =
  | "event_brief" | "music" | "set" | "transitions" | "visuals"
  | "lighting" | "client_approval" | "technical_rider" | "rehearsal" | "export" | "post_show";

/** Completion status of a single gig pack checklist item. */
export type DJSEMGigPackChecklistStatus = "todo" | "in_progress" | "blocked" | "done" | "not_applicable";

/** A single actionable task within a gig pack's preparation checklist. */
export interface DJSEMGigPackChecklistItem {
  id: string;
  category: DJSEMGigPackChecklistCategory;
  title: string;
  description?: string;
  status: DJSEMGigPackChecklistStatus;
  blocking: boolean;
  linkedResourceType?: string;
  linkedResourceId?: string;
  warnings: string[];
}

/** Aggregated per-category and overall readiness scoring for a gig pack. */
export interface DJSEMGigPackReadiness {
  overallScore: number;
  eventBriefReady: boolean;
  musicReady: boolean;
  transitionsReady: boolean;
  visualsReady: boolean;
  clientApprovalReady: boolean;
  technicalRiderReady: boolean;
  rehearsalReady: boolean;
  exportReady: boolean;
  warnings: string[];
}

/** A single gig's consolidated preparation bundle: linked sets, crates, brand/show assets, checklist, and readiness. */
export interface DJSEMGigPack {
  id: string;
  projectId: string;
  title: string;
  gigPackType: DJSEMGigPackType;
  eventBriefId?: string;
  setIds: string[];
  smartCrateIds: string[];
  brandWorldId?: string;
  showPackId?: string;
  clientPortalLinkIds: string[];
  status: string;
  readiness: DJSEMGigPackReadiness;
  checklist: DJSEMGigPackChecklistItem[];
  warnings: string[];
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

/** A manifest describing which resources were bundled (or excluded) when a gig pack was exported. */
export interface DJSEMGigPackExportManifest {
  id: string;
  gigPackId: string;
  exportType: "internal_manifest" | "client_pdf" | "venue_zip" | "operator_json" | "full_archive";
  includedResourceIds: string[];
  excludedResourceIds: string[];
  warnings: string[];
  hash?: string;
  createdAt: string;
}

/** Input required to create a new {@link DJSEMGigPack}. */
export interface CreateGigPackInput {
  projectId: string; title: string; gigPackType: DJSEMGigPackType;
  eventBriefId?: string; createdBy?: string;
}

const GIG_TEMPLATES: Record<DJSEMGigPackType, DJSEMGigPackChecklistItem[]> = {
  wedding: [
    { id: "gc_1", category: "event_brief", title: "Complete event brief", status: "todo", blocking: true, warnings: [] },
    { id: "gc_2", category: "music", title: "Build must-play crate", status: "todo", blocking: true, warnings: [] },
    { id: "gc_3", category: "music", title: "Build do-not-play list", status: "todo", blocking: false, warnings: [] },
    { id: "gc_4", category: "set", title: "Build ceremony/dinner/party sets", status: "todo", blocking: true, warnings: [] },
    { id: "gc_5", category: "client_approval", title: "Send client preview", status: "todo", blocking: true, warnings: [] },
    { id: "gc_6", category: "visuals", title: "Prepare intro screen", status: "todo", blocking: false, warnings: [] },
    { id: "gc_7", category: "technical_rider", title: "Confirm venue details", status: "todo", blocking: true, warnings: [] },
  ],
  bar: [
    { id: "gc_1", category: "music", title: "Build warmup/background crate", status: "todo", blocking: true, warnings: [] },
    { id: "gc_2", category: "set", title: "Build background set", status: "todo", blocking: false, warnings: [] },
  ],
  small_club: [
    { id: "gc_1", category: "music", title: "Build peak-time crate", status: "todo", blocking: true, warnings: [] },
    { id: "gc_2", category: "set", title: "Build main set", status: "todo", blocking: true, warnings: [] },
    { id: "gc_3", category: "transitions", title: "Review transitions", status: "todo", blocking: false, warnings: [] },
    { id: "gc_4", category: "visuals", title: "Prepare visuals if venue supports", status: "todo", blocking: false, warnings: [] },
  ],
  festival: [{ id: "gc_1", category: "set", title: "Build festival set", status: "todo", blocking: true, warnings: [] }, { id: "gc_2", category: "technical_rider", title: "Submit technical rider", status: "todo", blocking: true, warnings: [] }],
  corporate: [{ id: "gc_1", category: "event_brief", title: "Get client brief", status: "todo", blocking: true, warnings: [] }, { id: "gc_2", category: "music", title: "Build clean tracks crate", status: "todo", blocking: true, warnings: [] }, { id: "gc_3", category: "client_approval", title: "Client approval", status: "todo", blocking: true, warnings: [] }],
  private_party: [{ id: "gc_1", category: "music", title: "Build party crate", status: "todo", blocking: true, warnings: [] }],
  release_show: [{ id: "gc_1", category: "set", title: "Build release set", status: "todo", blocking: true, warnings: [] }, { id: "gc_2", category: "visuals", title: "Prepare release visuals", status: "todo", blocking: true, warnings: [] }],
  streaming_set: [{ id: "gc_1", category: "set", title: "Build streaming set", status: "todo", blocking: true, warnings: [] }, { id: "gc_2", category: "visuals", title: "Prepare stream visuals", status: "todo", blocking: true, warnings: [] }],
  venue_residency: [{ id: "gc_1", category: "music", title: "Build rotation crates", status: "todo", blocking: true, warnings: [] }],
  custom: [],
};

/** Creates and manages gig packs: checklist generation, readiness scoring, resource linking, and export manifests. */
export class DJSEMGigPackService {
  /**
   * Creates a new gig pack, generating its checklist template and initial readiness from the given type.
   * @param input - project, title, gig pack type, and optional linked event brief.
   * @returns the newly created gig pack in `"draft"` status (or `"needs_info"` if no event brief is linked).
   */
  createGigPack(input: CreateGigPackInput): DJSEMGigPack {
    const checklist = this.generateChecklist(input.gigPackType);
    const readiness = this.calculateReadiness(checklist, input.eventBriefId);
    return {
      id: `gp_${_seq.next()}`, projectId: input.projectId, title: input.title,
      gigPackType: input.gigPackType, eventBriefId: input.eventBriefId,
      setIds: [], smartCrateIds: [], clientPortalLinkIds: [],
      status: input.eventBriefId ? "draft" : "needs_info",
      readiness, checklist, warnings: [],
      createdBy: input.createdBy, createdAt: AT, updatedAt: AT,
    };
  }

  /**
   * Produces a fresh checklist from the template for the given gig pack type.
   * @param gigPackType - the gig pack type whose template to instantiate.
   * @returns a new list of checklist items (each `"todo"`), or an empty list for unknown/`"custom"` types.
   */
  generateChecklist(gigPackType: DJSEMGigPackType): DJSEMGigPackChecklistItem[] {
    const template = GIG_TEMPLATES[gigPackType] ?? [];
    return template.map((item, i) => ({ ...item, id: `gc_${_seq.next()}` }));
  }

  /**
   * Computes per-category and overall readiness from a checklist's current statuses.
   * @param checklist - the current checklist items.
   * @param eventBriefId - the linked event brief id, if any (missing id degrades `eventBriefReady` and adds a warning).
   * @returns the readiness scoring, including any warnings about missing links or outstanding blocking items.
   */
  calculateReadiness(checklist: DJSEMGigPackChecklistItem[], eventBriefId?: string): DJSEMGigPackReadiness {
    const done = checklist.filter(c => c.status === "done" || c.status === "not_applicable").length;
    const total = checklist.length;
    const blocking = checklist.filter(c => c.blocking && c.status !== "done" && c.status !== "not_applicable");
    const warnings: string[] = [];
    if (!eventBriefId) warnings.push("No event brief linked — some readiness checks skipped.");
    if (blocking.length > 0) warnings.push(`${blocking.length} blocking item(s) outstanding.`);
    const catReady = (cat: DJSEMGigPackChecklistCategory) => checklist.filter(c => c.category === cat).every(c => c.status === "done" || c.status === "not_applicable");
    return {
      overallScore: total > 0 ? done / total : 0,
      eventBriefReady: !!eventBriefId && catReady("event_brief"),
      musicReady: catReady("music"),
      transitionsReady: catReady("transitions"),
      visualsReady: catReady("visuals"),
      clientApprovalReady: catReady("client_approval"),
      technicalRiderReady: catReady("technical_rider"),
      rehearsalReady: catReady("rehearsal"),
      exportReady: blocking.length === 0,
      warnings,
    };
  }

  /**
   * Updates a single checklist item's status and recomputes the gig pack's readiness.
   * @param gp - the gig pack to update.
   * @param itemId - id of the checklist item to update.
   * @param status - the new status for the item.
   * @returns a copy of the gig pack with the updated checklist and recalculated readiness.
   */
  markChecklistItem(gp: DJSEMGigPack, itemId: string, status: DJSEMGigPackChecklistStatus): DJSEMGigPack {
    const checklist = gp.checklist.map(c => c.id === itemId ? { ...c, status } : c);
    return { ...gp, checklist, readiness: this.calculateReadiness(checklist, gp.eventBriefId), updatedAt: AT };
  }

  /**
   * Links an external resource (set, smart crate, or show pack) to a gig pack.
   * @param gp - the gig pack to update.
   * @param resourceType - the kind of resource being linked.
   * @param resourceId - id of the resource to link.
   * @returns a copy of the gig pack with the resource linked; unchanged if `resourceType` is unrecognized.
   */
  linkResource(gp: DJSEMGigPack, resourceType: "set" | "crate" | "showPack", resourceId: string): DJSEMGigPack {
    if (resourceType === "set") return { ...gp, setIds: [...gp.setIds, resourceId], updatedAt: AT };
    if (resourceType === "crate") return { ...gp, smartCrateIds: [...gp.smartCrateIds, resourceId], updatedAt: AT };
    if (resourceType === "showPack") return { ...gp, showPackId: resourceId, updatedAt: AT };
    return gp;
  }

  /**
   * Builds an export manifest listing which resources a gig pack export would include.
   * @param gp - the gig pack being exported.
   * @param exportType - the export format/target.
   * @returns the export manifest with included resource ids and the gig pack's current warnings.
   */
  createExportManifest(gp: DJSEMGigPack, exportType: DJSEMGigPackExportManifest["exportType"]): DJSEMGigPackExportManifest {
    return {
      id: `gpm_${_seq.next()}`, gigPackId: gp.id, exportType,
      includedResourceIds: [...gp.setIds, ...gp.smartCrateIds, ...(gp.showPackId ? [gp.showPackId] : [])],
      excludedResourceIds: [], warnings: gp.warnings,
      createdAt: AT,
    };
  }
}
